import * as React from 'react';
import { Dispatch, SetStateAction, useCallback, useState, useMemo, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Box,
  RadioGroup, Alert
} from '@mui/material';
import { paintRegionsOptions, heatmapPropertiesDetails } from './data';
import * as state from '@/components/Analysis/state';
import {
  CustomizationPanel,
  CustomRadio,
  CustomSwitch,
  DeckPlanFormControl
} from './styles';
import {
  useFetchSpatialHeatmapLazyQuery,
  ModeInput,
  Maybe,
  useDefectLegendsDeckSummaryLazyQuery,
} from '@/__generated__/graphql';
import { useHavePermission } from '@/hooks';
import { HeatmapValueEnum, Permissions, TemplateWithPOIs } from '@/types';
import { primary } from '@/theme/colors';
import { theme } from '@/theme/theme';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { EmptyHeatMap } from './EmptyHeatMap';
import { snackbarMessage } from '@/state';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { titleCase } from '@/utils';
import { DeckplanPoiToggle } from './DeckplanPoiToggle';
import { DeckplanOverlayWorkscopeToggle } from '../DeckPlanOverlay/components/DeckplanOverlayWorkscopeToggle';

const valueText = (value: number) => {
  return `${value}`;
};

type Deck = {
  id: string;
  resourcePath: string;
  name: string;
  heightRange: number[];
  heightStep?: Maybe<number> | undefined;
};

type PaintRegion = 'paintBlockOutline' | 'paintBlockFilled';
type HeatmapDetail = 'block' | 'voxel';

type DefectLegend = {
  id: number;
  name: string;
  groupKey: string;
  groupName: string;
  color?: string;
};

type HeatMapValues = { id: number; groupKey: string; groupName: string }[];

type GroupedDefects = { [key: string]: { id: number; groupKey: string; groupName: string } };

type Props = {
  selectedDeck: Deck;
  isQueryCleared: boolean;
  isButtonDisabled: boolean;
  allTemplatesWithPOIs: TemplateWithPOIs[];
  poisLoading: boolean;
  imageError?: boolean;
};

const markerScaleMin = 2;
const markerScaleMax = 8;
const markerScaleSteps = 20;

export const DeckPlanCustomizationPanel = ({
  selectedDeck,
  isQueryCleared,
  isButtonDisabled,
  allTemplatesWithPOIs,
  poisLoading,
  imageError = false,
}: Props) => {
  const [cameraPositionsSwitch, setCameraPositionsSwitch] = useState(true);
  const [paintRegionSwitch, setPaintRegionSwitch] = useState(false);

  const canReadDefects = useHavePermission(Permissions.READ_DEFECTS);

  const [activeHeightRange, setActiveHeightRange] = useState<number[]>([0, 20]);
  const [paintRegion, setPaintRegion] = useState<PaintRegion>('paintBlockOutline');
  const [heatmapDetail, setHeatmapDetail] = useState<HeatmapDetail>('block');
  const [heatmapValue, setHeatmapValue] = useState<string>('');
  const setAppliedHeatmap = useSetAtom(state.appliedHeatmapValue);
  const [isHeatmapAvailable, setIsHeatmapAvailable] = useState(true);
  const  setSelectedTemplates = useSetAtom(state.pointOfInterestTemplates);
  const [selectedDefects, setSelectedDefects] = useState<string[]>([]);
  const setSnackbarMessage = useSetAtom(snackbarMessage);
  const setShowPaintRegions = useSetAtom(state.showPaintRegionsState);
  const setShowCameraPositions = useSetAtom(state.showCameraPositionsState);
  const [activeMarkerScaleRange, setActiveMarkerScaleRange] = useAtom(state.markerScaleSize);
  const [defectLegends, setDefectLegends] = useState<DefectLegend[] | undefined>([]);
  const [heatmapPropertiesValues, setHeatmapPropertiesValues] = useState<HeatMapValues>([]);
  const structureId = useGetStructureId();
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);
  const [heatmapSvg, setHeatmapSvg] = useAtom(state.heatmapSvgState);
  const setPaintRegionOutline = useSetAtom(state.paintRegionOutline);
  const navbarHeight = useNavbarHeight();
  const [fetchDefectLegends] = useDefectLegendsDeckSummaryLazyQuery();

  useEffect(() => {
    fetchDefectLegends({
      variables: {
        structureId,
      },
      fetchPolicy: 'cache-first',
    }).then(({ data }) => {
      const defects = data?.structure?.defectLegends;

      if (defects) {
        // Group defect legends by groupKey
        const groupedDefects: GroupedDefects = defects.reduce((accumulator, defect) => {
          const { groupKey, groupName, id } = defect;
          if (!accumulator[groupKey]) {
            accumulator[groupKey] = {
              id, // Unique id for each groupKey
              groupKey,
              groupName,
            };
          }
          return accumulator;
        }, {} as GroupedDefects); // Ensure the correct typing

        // Convert the groupedDefects object into an array
        const heatmapValues = Object.values(groupedDefects);
        setHeatmapPropertiesValues(heatmapValues);
        setDefectLegends(defects);
        if (heatmapValues.length > 0) {
          setHeatmapValue(heatmapValues[0].groupKey);
        }
      }
    });
  }, [fetchDefectLegends, setHeatmapValue, structureId]);

  const filteredDefects = useMemo(() => {
    if (heatmapValue && defectLegends && defectLegends.length > 0) {
      setSelectedDefects([]);
      return defectLegends
        .filter((defect) => defect.groupKey === heatmapValue)
        .map((defect) => ({ key: defect.id, label: defect.name }));
    }
    return [];
  }, [heatmapValue, defectLegends]);

  // when the selected deck id is changed, we want to enable isHeatmapAvailable
  useEffect(() => {
    setIsHeatmapAvailable(true);
  }, [selectedDeck.id]);

  // TODO: will be implemented in future when lambda support heatmap for subfields need to enable checkbox for this
  /*
  const handleChangeDefect = (checked: boolean, defectKey: string) => {
    if (checked) {
      setSelectedDefects((previousDefects) => [...previousDefects, defectKey]);
    } else {
      setSelectedDefects((previousDefects) =>
        previousDefects.filter((selectedKey) => selectedKey !== defectKey)
      );
    }
  };
  */

  const noDefectsSelected = useMemo(() => {
    if (heatmapValue === HeatmapValueEnum.DEFECTS) {
      return selectedDefects.length === 0;
    }
    return false;
  }, [heatmapValue, selectedDefects]);

  useEffect(() => {
    if (heatmapValue !== HeatmapValueEnum.DEFECTS) return;
    setSelectedDefects([]);
  }, [heatmapValue]);

  useEffect(() => {
    setSelectedTemplates((previousSelectedTemplates) =>
      allTemplatesWithPOIs.filter((template) =>
        previousSelectedTemplates.some(({ id }) => id === template.id)
      )
    );
  }, [allTemplatesWithPOIs, setSelectedTemplates]);

  const modes = useMemo(() => {
    let heatmapModes = [heatmapValue] as string[];
    let defects: string[] = [];
    if (heatmapValue === HeatmapValueEnum.DEFECTS) {
      heatmapModes = selectedDefects;
      defects = selectedDefects;
    }
    return {
      heightRange: activeHeightRange,
      modes: heatmapModes,
      type: heatmapDetail,
      defects,
    } as ModeInput;
  }, [activeHeightRange, heatmapDetail, heatmapValue, selectedDefects]);
  const heightMarks = [
    {
      value: selectedDeck?.heightRange[0] ?? 0,
      label: selectedDeck?.heightRange[0] ?? '0',
    },
    {
      value: selectedDeck?.heightRange[1] ?? 20,
      label: selectedDeck?.heightRange[1] ?? '20',
    },
  ];

  const [getSpatialHeatmap, { error, data, loading }] = useFetchSpatialHeatmapLazyQuery({
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (data?.fetchSpatialHeatmap?.svg) {
      if (modes.modes.includes(heatmapValue)) {
        setHeatmapSvg(data.fetchSpatialHeatmap.svg);
      } else {
        setPaintRegionOutline(data?.fetchSpatialHeatmap?.svg);
      }
    }

    if (error) {
      setIsHeatmapAvailable(false);
      setHeatmapSvg('');
      setPaintRegionOutline('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error]);

  useEffect(() => {
    setHeatmapSvg('');
    setPaintRegionOutline('');
    if (selectedDeck?.heightRange) {
      setActiveHeightRange([selectedDeck?.heightRange[0], selectedDeck?.heightRange[1]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeck]);

  useEffect(() => {
    setHeatmapSvg('');
    setPaintRegionSwitch(false);
    setPaintRegion('paintBlockOutline');
    setPaintRegionOutline('');
    setSelectedTemplates([]);
  }, [
    isQueryCleared,
    setPaintRegionSwitch,
    setHeatmapSvg,
    setPaintRegionOutline,
    setPaintRegion,
    setSelectedTemplates,
  ]);

  const applySpatialHeatmap = useCallback(async () => {
    if (heatmapSvg === '' || modes.modes.includes(paintRegion)) {
      modes.modes = [heatmapValue];
    }
    const defectsHeatMap = heatmapValue === HeatmapValueEnum.DEFECTS;
    getSpatialHeatmap({
      variables: {
        heatmapParameters: {
          structureId,
          deckId: selectedDeck.id,
          modes: defectsHeatMap ? { ...modes, modes: selectedDefects } : modes,
          selectedTemplateFilters,
        },
      },
    }).then((response) => {
      const heatmapData = response.data?.fetchSpatialHeatmap;
      if (heatmapData) {
        setAppliedHeatmap(heatmapValue);
        return;
      }
      setSnackbarMessage({
        shouldShow: true,
        content: <Alert severity="error">Heatmap not available, try again in a while</Alert>,
      });
    });
  }, [
    heatmapSvg,
    modes,
    paintRegion,
    heatmapValue,
    getSpatialHeatmap,
    structureId,
    selectedDeck.id,
    selectedDefects,
    selectedTemplateFilters,
    setSnackbarMessage,
    setAppliedHeatmap,
  ]);

  const applyPaintRegions = useCallback(async () => {
    if (paintRegion === 'paintBlockOutline') {
      modes.modes = [paintRegion];
      getSpatialHeatmap({
        variables: {
          heatmapParameters: {
            structureId,
            deckId: selectedDeck.id,
            modes,
            selectedTemplateFilters,
          },
        },
      });
      setShowPaintRegions(false);
    } else {
      setShowPaintRegions(true);
    }
  }, [
    getSpatialHeatmap,
    modes,
    paintRegion,
    selectedDeck.id,
    selectedTemplateFilters,
    setShowPaintRegions,
    structureId,
  ]);

  useEffect(() => {
    if (paintRegionSwitch && !isQueryCleared) {
      applyPaintRegions();
    } else {
      modes.modes = [heatmapValue];
      setShowPaintRegions(false);
      setPaintRegionOutline('');
    }
  }, [
    heatmapValue,
    paintRegionSwitch,
    paintRegion,
    modes,
    applyPaintRegions,
    setShowPaintRegions,
    setPaintRegionOutline,
    isQueryCleared,
  ]);

  useEffect(() => {
    setShowCameraPositions(cameraPositionsSwitch);
  }, [cameraPositionsSwitch, setShowCameraPositions]);

  const handleCameraPositionsSwitch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCameraPositionsSwitch(event.target.checked);
    },
    [setCameraPositionsSwitch]
  );

  const handleActiveMarkerScaleRange = useCallback(
    (event: Event, value: number | number[]) => {
      setActiveMarkerScaleRange(value as number);
    },
    [setActiveMarkerScaleRange]
  );

  const handlePaintRegionSwitch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPaintRegionSwitch(event.target.checked);
    },
    [setPaintRegionSwitch]
  );

  const handleActiveHeightRangeChange = useCallback(
    (event: Event, value: number | number[]) => {
      setActiveHeightRange(value as number[]);
    },
    [setActiveHeightRange]
  );

  const handleRadioOptionsChange = useCallback(
    <T,>(event: React.ChangeEvent<HTMLInputElement>, setFunction: Dispatch<SetStateAction<T>>) => {
      setFunction((event.target as HTMLInputElement).value as T);
    },
    []
  );

  const cameraSwitch = useMemo(() => {
    return <CustomSwitch checked={cameraPositionsSwitch} onChange={handleCameraPositionsSwitch} />;
  }, [cameraPositionsSwitch, handleCameraPositionsSwitch]);

  return (
    <Box>
      <CustomizationPanel navbarHeight={navbarHeight}>
        <>
          <Typography sx={{ fontWeight: 700 }} variant="h6">
            View Customization
          </Typography>

          <DeckPlanFormControl
            control={cameraSwitch}
            label="Camera Positions"
            labelPlacement="start"
          />
          <FormLabel>Marker scale</FormLabel>
          <Slider
            value={activeMarkerScaleRange}
            onChange={handleActiveMarkerScaleRange}
            aria-label="Marker scale"
            getAriaValueText={valueText}
            step={markerScaleMax / markerScaleSteps}
            min={markerScaleMin}
            max={markerScaleMax}
            sx={{
              color: theme.palette.common.darkBlue,
            }}
            size="small"
          />
          <Divider />
          <DeckPlanFormControl
            control={
              <CustomSwitch checked={paintRegionSwitch} onChange={handlePaintRegionSwitch} />
            }
            label="Paint Regions"
            labelPlacement="start"
          />
          {paintRegionSwitch && (
            <RadioGroup
              value={paintRegion}
              onChange={(event) => handleRadioOptionsChange<PaintRegion>(event, setPaintRegion)}
            >
              {paintRegionsOptions.map((paintRegionOption) => (
                <FormControlLabel
                  key={paintRegionOption.id}
                  value={paintRegionOption.value}
                  control={<CustomRadio />}
                  label={paintRegionOption.label}
                  disabled={loading}
                />
              ))}
            </RadioGroup>
          )}

          <Divider />
          <DeckplanPoiToggle allTemplatesWithPOIs={allTemplatesWithPOIs} loading={poisLoading} />
          <DeckplanOverlayWorkscopeToggle />

          {imageError && (
            <EmptyHeatMap
              doesSelectedFiltersHeatMapExist={
                !isHeatmapAvailable && selectedTemplateFilters.length > 0
              }
            />
          )}

          {!imageError && (
            <>
              <Typography sx={{ fontWeight: 700, marginBottom: 1, marginTop: 1 }} variant="body2">
                Heatmap Properties
              </Typography>
              <FormControl>
                <FormLabel>Detail</FormLabel>
                <RadioGroup
                  value={heatmapDetail}
                  onChange={(event) => {
                    return handleRadioOptionsChange<HeatmapDetail>(event, setHeatmapDetail);
                  }}
                >
                  {heatmapPropertiesDetails.map(
                    (heatmapPropertiesDetail) =>
                      (!heatmapPropertiesDetail.defectVisibilityPermission ||
                        canReadDefects === heatmapPropertiesDetail.defectVisibilityPermission) && (
                        <FormControlLabel
                          key={heatmapPropertiesDetail.id}
                          value={heatmapPropertiesDetail.value}
                          control={<CustomRadio />}
                          label={heatmapPropertiesDetail.label}
                        />
                      )
                  )}
                </RadioGroup>
              </FormControl>
              <FormControl>
                <FormLabel>Value</FormLabel>
                <RadioGroup
                  value={heatmapValue}
                  onChange={(event) => {
                    handleRadioOptionsChange<string>(event, setHeatmapValue);
                  }}
                >
                  {canReadDefects &&
                    heatmapPropertiesValues.map((heatmapPropertiesValue) => (
                      <>
                        <FormControlLabel
                          key={heatmapPropertiesValue.id}
                          value={heatmapPropertiesValue.groupKey}
                          control={<CustomRadio />}
                          label={titleCase(heatmapPropertiesValue.groupName)}
                        />
                        {heatmapValue === heatmapPropertiesValue.groupKey && (
                          <Box sx={{ ml: 3, mb: 1 }}>
                            {filteredDefects?.map((defect) => (
                              <Typography key={defect.key} variant="body1">
                                {defect.label}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </>
                    ))}
                </RadioGroup>
              </FormControl>

              {/*  TODO: will be implemented in future when lambda support heatmap for subfields
                <FormControl sx={{ ml: 3, mb: 1 }}>
                  {filteredDefects?.map((defect) => (
                    <FormControlLabel
                      key={defect.key}
                      control={<Checkbox />}
                      label={defect.label}
                      onChange={(_, checked) => handleChangeDefect(checked, defect.label)}
                    />
                  ))}
                </FormControl> */}
              <FormControl>
                <FormLabel>Height</FormLabel>
                <Slider
                  value={activeHeightRange}
                  onChange={handleActiveHeightRangeChange}
                  getAriaValueText={valueText}
                  valueLabelDisplay="auto"
                  step={selectedDeck?.heightStep ?? 1}
                  min={selectedDeck?.heightRange[0] ?? 0}
                  max={selectedDeck?.heightRange[1] ?? 20}
                  marks={heightMarks}
                  sx={{
                    color: theme.palette.common.darkBlue,
                  }}
                  size="small"
                />
              </FormControl>
            </>
          )}
        </>
      </CustomizationPanel>
      {!imageError && (
        <Box sx={{ padding: 2, borderTop: '1px solid #D4DDE8' }}>
          <Button
            id="deckplan-image-query-button"
            variant="contained"
            size="large"
            fullWidth
            onClick={() => applySpatialHeatmap()}
            disabled={loading || isButtonDisabled || noDefectsSelected}
            sx={{
              background: primary.darkBlue,
              height: '42px',
              borderRadius: 0.5,
              fontSize: '1.5rem',
              fontWeight: 500,
              '@media (max-width: 1728px)': {
                height: '32px',
                padding: '0',
              },
            }}
          >
            {loading ? (
              <CircularProgress sx={{ color: '#fff' }} size="2.4rem" />
            ) : (
              <> APPLY CHANGES </>
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};
